import { useFragment } from "@/generated";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { LessonFieldsFragmentDoc, MoveLessonsToTrashDocument } from "../../../generated/graphql";

/**
 * Hook for moving lessons to trash (soft delete).
 *
 * @example
 * const { moveLessonsToTrash, loading, error } = useMoveLessonToTrash();
 * // Move single lesson
 * await moveLessonsToTrash({ variables: { ids: ["lesson123"] } });
 * // Move multiple lessons
 * await moveLessonsToTrash({ variables: { ids: ["lesson1", "lesson2", "lesson3"] } });
 */
export function useMoveLessonToTrash() {
  const [moveLessonsToTrashMutation, { loading, error }] = useMutation(MoveLessonsToTrashDocument, {
    update(cache, { data }) {
      if (!data?.moveLessonsToTrash) return;

      try {
        const lessons = data.moveLessonsToTrash.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        const lessonsByParentIdMap = new Map<string | null, typeof lessons>();

        for (const lesson of lessons) {
          const key = lesson.parentId ?? null;

          const lessonsByParent = lessonsByParentIdMap.get(key) ?? [];
          lessonsByParent.push(lesson);
          lessonsByParentIdMap.set(key, lessonsByParent);
        }

        cache.batch({
          update(cache) {
            for (const lesson of lessons) {
              const id = cache.identify(lesson);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: LessonFieldsFragmentDoc,
                  data: lesson,
                });
              }

              // Update lesson isTrash => true
              cache.modify({
                id: `Lesson:${lesson.id}`,
                fields: {
                  isTrash() {
                    return true;
                  },
                },
              });
            }

            for (const [parentId, lessons] of lessonsByParentIdMap.entries()) {
              const newRefs = lessons
                .map((p) => cache.identify(p))
                .filter(Boolean)
                .map((id) => ({ __ref: id }));

              // Add lessons to trash page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  lessonsByParentId(existingData, { storeFieldName }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":true';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: [...newRefs, ...existingData.items],
                      total: (existingData.total || 0) + newRefs.length,
                    };
                  },
                },
              });

              // Substracts parent itemCount
              if (parentId !== null) {
                cache.modify({
                  id: `Project:${parentId}`,
                  fields: {
                    itemCount(existingData = 0) {
                      return existingData - newRefs.length;
                    },

                    trashCount(existingData = 0) {
                      return existingData + newRefs.length;
                    },
                  },
                });
              }

              // Remove lesson from main page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  lessonsByParentId(existingData, { storeFieldName, readField }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":false';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: existingData.items.filter(
                        (ref: Reference) => !lessons.some((p) => readField("id", ref) === p.id)
                      ),
                      total: (existingData.total || 0) - newRefs.length,
                    };
                  },
                },
              });

              // Increment Trash count number
              if (parentId === null) {
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    rootTrashCount(existingData) {
                      return existingData + newRefs.length;
                    },
                  },
                });
              }
            }
          },
        });
      } catch (error) {
        console.error("Move lessons to trash cache update failed:", error);
      }
    },
  });

  const moveLessonsToTrash = async (lessonIds: string[], skipCheck: boolean) => {
    return withToast(
      async () => {
        const result = await moveLessonsToTrashMutation({
          variables: {
            ids: lessonIds,
            skipCheck,
          },
        });

        const maskedLessons = result.data?.moveLessonsToTrash ?? [];
        const trashedLessons = maskedLessons.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        return trashedLessons;
      },
      {
        loading: `Moving ${lessonIds.length} lesson${lessonIds.length !== 1 ? "s" : ""} to trash...`,
        success: `Successfully moved ${lessonIds.length} lesson${lessonIds.length !== 1 ? "s" : ""} to trash`,
        error: "Failed to move lessons to trash. Please try again.",
      },
      "moveLessonsToTrash"
    );
  };

  return { moveLessonsToTrash, loading, error };
}
